import type {
  BillingViewModel,
  BuildFeeViewModel,
  PaymentViewModel,
  SiteExportViewModel,
  SubscribeViewModel,
  TicketListViewModel,
  UsageViewModel,
} from "@lumora/core";
import { PlanCatalog, PlanCatalogViewModel } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { client, SITE } from "../client.js";
import { DashboardPage } from "./DashboardPage.js";
import { canViewLogs } from "./logsView.js";
import { NewCustomerSection } from "./NewCustomerSection.js";
import { canSetUpPayouts } from "./PayoutsSection.js";

type PortalTab = "new" | "existing";

export interface PortalPageProps {
  subscribeVM: SubscribeViewModel;
  paymentVM: PaymentViewModel;
  buildFeeVM: BuildFeeViewModel;
  ticketsVM: TicketListViewModel;
  usageVM: UsageViewModel;
  billingVM: BillingViewModel;
  siteExportVM: SiteExportViewModel;
  publishableKey: string | undefined;
  returnUrl: string;
  /** Owner site slug (from last sign-in / signup). Display only. */
  siteSlug?: string | undefined;
  /** Sign-in security block (MFA banner + panel); the app passes it so tests need no network. */
  securitySlot?: ReactNode;
  className?: string;
}

/**
 * PortalPage switches between hosting setup (new) and the signed-in owner
 * dashboard (existing). Pure view state for the tab; VMs own business logic.
 */
export function PortalPage({
  subscribeVM,
  paymentVM,
  buildFeeVM,
  ticketsVM,
  usageVM,
  billingVM,
  siteExportVM,
  publishableKey,
  returnUrl,
  siteSlug,
  securitySlot,
  className,
}: PortalPageProps) {
  const [searchParams] = useSearchParams();
  const role = client.claims()?.role ?? null;
  const pairCode = searchParams.get("pair") ?? undefined;
  const [tab, setTab] = useState<PortalTab>("existing");
  const billing = useViewModel(billingVM);
  const buildFee = useViewModel(buildFeeVM);
  const catalogVM = useMemo(
    () => new PlanCatalogViewModel(client, siteSlug ?? SITE),
    [siteSlug],
  );
  const catalog = useViewModel(catalogVM);
  const hostingPlans = useMemo(() => {
    const wanted = new Set(["payg", "starter", "growth", "pro"]);
    const loaded = catalog.plans.filter((p) => wanted.has(p.key));
    return loaded.length >= 4 ? loaded : PlanCatalog.backendHostingPlans();
  }, [catalog.plans]);
  const billingConfigured = billing.status?.billingConfigured !== false;
  const showAddHosting =
    billingConfigured && (billing.status === null || billing.status.needsPayment);

  useEffect(() => {
    void catalogVM.load();
  }, [catalogVM]);

  useEffect(() => {
    void billingVM.loadStatus();
  }, [billingVM]);

  useEffect(() => {
    void buildFeeVM.load();
  }, [buildFeeVM]);

  useEffect(() => {
    if (!showAddHosting && tab === "new") setTab("existing");
  }, [showAddHosting, tab]);

  return (
    <div className={`${className ?? ""} site-portal-sheet`.trim()} data-lumora-view="portal">
      <p className="site-signin-kicker">Owner portal</p>
      <h1>{tab === "new" ? "Start hosting" : "Your dashboard"}</h1>
      <p className="portal-lead">
        {tab === "new"
          ? buildFeeVM.hasQuotedFee
            ? "Choose a backend hosting plan to set up billing and pay the one-time build fee."
            : "Choose a backend hosting plan to set up payment and activate hosting in one step."
          : siteSlug
            ? `Tickets, usage, and billing for ${siteSlug}.`
            : "Tickets, usage, and billing for your site."}
      </p>
      <div role="tablist" className="portal-tabs" data-lumora-portal-tabs>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "existing"}
          data-active={tab === "existing" ? "true" : undefined}
          onClick={() => setTab("existing")}
        >
          Manage site
        </button>
        {showAddHosting ? (
          <button
            type="button"
            role="tab"
            aria-selected={tab === "new"}
            data-active={tab === "new" ? "true" : undefined}
            onClick={() => setTab("new")}
          >
            Add hosting
          </button>
        ) : null}
      </div>

      {tab === "new" ? (
        <NewCustomerSection
          subscribeVM={subscribeVM}
          paymentVM={paymentVM}
          buildFeeVM={buildFeeVM}
          publishableKey={publishableKey}
          returnUrl={returnUrl}
          hostingPlans={hostingPlans}
        />
      ) : (
        <DashboardPage
          ticketsVM={ticketsVM}
          usageVM={usageVM}
          billingVM={billingVM}
          siteExportVM={siteExportVM}
          siteSlug={siteSlug}
          pairCode={pairCode}
          onStartHosting={showAddHosting ? () => setTab("new") : undefined}
          logsClient={canViewLogs(role) ? client : undefined}
          payoutsClient={canSetUpPayouts(role) ? client : undefined}
          securitySlot={securitySlot}
        />
      )}
    </div>
  );
}
